import { useState, useEffect, useCallback } from "react";
import {
  BarChart, Bar, ComposedChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell,
} from "recharts";
import inventoryApi from "./inventoryApi";

/* ─────────────────────────────────────────────
   CONSTANTS
───────────────────────────────────────────── */
const TEAL    = "#11a8a4";
const PURPLE  = "#9440dd";
const GREEN   = "#16a34a";
const AMBER   = "#f59e0b";
const RED     = "#dc2626";
const CYAN    = "#06b6d4";
const VIOLET  = "#8b5cf6";

const MONTH_NAMES = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const MEDAL_COLORS = [
  { border: "#f59e0b", bg: "#fffbeb", label: "🥇" },
  { border: "#9ca3af", bg: "#f9fafb", label: "🥈" },
  { border: "#b45309", bg: "#fff7ed", label: "🥉" },
];

const CAMP_BAR_COLORS = [TEAL, PURPLE, AMBER, CYAN, VIOLET];

/* ─────────────────────────────────────────────
   GENERIC HELPERS
───────────────────────────────────────────── */
const normalizeArr = (raw) => (Array.isArray(raw) ? raw : (raw?.results ?? []));

function useFetch(fetcher, deps) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const run = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const raw = await fetcher();
      setData(raw);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Failed to load data.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    run();
  }, [run]);

  return { data, loading, error, refetch: run };
}

function buildCsv(rows, headers) {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const lines = [headers.map(esc).join(",")];
  rows.forEach((r) => lines.push(headers.map((h) => esc(r[h])).join(",")));
  return lines.join("\n");
}

function downloadCsv(filename, csv) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function bagLabel(b) {
  const techName =
    b.technician_name ??
    (typeof b.technician === "string" ? b.technician : b.technician?.name) ??
    `Tech #${b.technician_id ?? b.technician ?? "?"}`;
  return `${techName} — Bag #${b.id}`;
}

/* ─────────────────────────────────────────────
   GENERIC SECTION WRAPPER
───────────────────────────────────────────── */
function Section({ title, accent = TEAL, children, action }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
      <div
        className="flex items-center justify-between px-5 py-4 border-b border-slate-100 gap-3 flex-wrap"
        style={{ background: `linear-gradient(90deg, ${accent}12, transparent)` }}
      >
        <div className="flex items-center gap-2.5">
          <span className="w-1 h-5 rounded-full flex-shrink-0" style={{ backgroundColor: accent }} />
          <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wide">{title}</h2>
        </div>
        {action}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   SKELETONS / EMPTY / ERROR
───────────────────────────────────────────── */
function Skeleton({ h = "h-4", w = "w-full", className = "" }) {
  return <div className={`${h} ${w} bg-slate-200 rounded-lg animate-pulse ${className}`} />;
}

function ChartSkeleton() {
  return (
    <div className="space-y-3">
      <div className="flex items-end gap-3 h-48">
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className="flex-1 bg-slate-200 rounded-t-md animate-pulse"
            style={{ height: `${40 + Math.random() * 60}%`, animationDelay: `${i * 80}ms` }}
          />
        ))}
      </div>
      <div className="flex gap-6 justify-center">
        <Skeleton h="h-3" w="w-24" />
        <Skeleton h="h-3" w="w-24" />
      </div>
    </div>
  );
}

function TableSkeleton({ rows = 5, cols = 4 }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex gap-3" style={{ animationDelay: `${i * 60}ms` }}>
          {Array.from({ length: cols }).map((_, j) => (
            <Skeleton key={j} h="h-8" w={j === 0 ? "w-8" : "w-full"} />
          ))}
        </div>
      ))}
    </div>
  );
}

function ErrorBlock({ message, onRetry }) {
  return (
    <div className="flex flex-col items-center justify-center py-10 gap-3 text-center">
      <div className="w-10 h-10 rounded-xl bg-rose-50 border border-rose-200 flex items-center justify-center text-lg">
        ⚠
      </div>
      <p className="text-sm text-slate-500">{message || "Failed to load data."}</p>
      <button
        onClick={onRetry}
        className="px-4 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold transition-colors"
      >
        Retry
      </button>
    </div>
  );
}

function EmptyState({ text, icon = "📭" }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-slate-400 gap-2">
      <span className="text-3xl">{icon}</span>
      <p className="text-sm font-medium">{text}</p>
    </div>
  );
}

/* ─────────────────────────────────────────────
   CUSTOM RECHARTS TOOLTIP
───────────────────────────────────────────── */
function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-900 text-white text-xs rounded-xl shadow-xl px-3.5 py-2.5 border border-slate-700">
      {label != null && <p className="font-bold mb-1.5 text-slate-300">{label}</p>}
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2 mb-0.5">
          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: p.color || p.payload?.color }} />
          <span className="text-slate-400">{p.name}:</span>
          <span className="font-bold">{Number(p.value)?.toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────
   SMALL SHARED UI ATOMS
───────────────────────────────────────────── */
function StatTile({ label, value, accent = "#475569", sub }) {
  return (
    <div className="rounded-xl bg-slate-50 px-3 py-3 text-center">
      <p className="text-base font-bold tabular-nums" style={{ color: accent }}>{value}</p>
      <p className="text-[10px] text-slate-400 uppercase tracking-wide mt-0.5">{label}</p>
      {sub && <p className="text-[10px] text-slate-400 mt-0.5">{sub}</p>}
    </div>
  );
}

function MiniStat({ label, value, accent = "#475569" }) {
  return (
    <div className="text-center">
      <p className="text-lg font-bold tabular-nums" style={{ color: accent }}>{value}</p>
      <p className="text-[10px] text-slate-400 uppercase tracking-wide">{label}</p>
    </div>
  );
}

function Badge({ color, label, value }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold border"
      style={{ borderColor: `${color}40`, backgroundColor: `${color}10`, color }}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: color }} />
      {label}: <span className="font-bold">{value}</span>
    </span>
  );
}

function LegendDot({ color, label }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-slate-500">
      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
      {label}
    </span>
  );
}

function CategoryBadge({ category }) {
  if (category === "CONSUMABLE")
    return (
      <span className="inline-flex px-2 py-0.5 rounded-md text-[10px] font-bold bg-violet-100 text-violet-700 border border-violet-200 uppercase tracking-wide">
        Cons.
      </span>
    );
  if (category === "CAPITAL")
    return (
      <span className="inline-flex px-2 py-0.5 rounded-md text-[10px] font-bold bg-amber-100 text-amber-700 border border-amber-200 uppercase tracking-wide">
        Cap.
      </span>
    );
  return (
    <span className="inline-flex px-2 py-0.5 rounded-md text-[10px] font-bold bg-slate-100 text-slate-500 uppercase tracking-wide">
      {category ?? "—"}
    </span>
  );
}

function StatusPill({ status }) {
  const map = {
    Submitted: { cls: "bg-emerald-100 text-emerald-700 border-emerald-200", dot: GREEN },
    Pending:   { cls: "bg-rose-100 text-rose-700 border-rose-200",          dot: RED },
    VERIFIED:  { cls: "bg-emerald-100 text-emerald-700 border-emerald-200", dot: GREEN },
    MISMATCH:  { cls: "bg-rose-100 text-rose-700 border-rose-200",          dot: RED },
  };
  const cfg = map[status] ?? { cls: "bg-amber-100 text-amber-700 border-amber-200", dot: AMBER };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-bold border whitespace-nowrap ${cfg.cls}`}>
      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: cfg.dot }} />
      {status ?? "PENDING"}
    </span>
  );
}

function CircularProgress({ pct = 0, size = 100, stroke = 9, color = TEAL, label }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const clamped = Math.min(Math.max(pct, 0), 100);
  const offset = c - (clamped / 100) * c;
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        <circle cx={size / 2} cy={size / 2} r={r} stroke="#f1f5f9" strokeWidth={stroke} fill="none" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke={color}
          strokeWidth={stroke}
          fill="none"
          strokeDasharray={c}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 0.6s ease" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-lg font-bold text-slate-800">{Math.round(clamped)}%</span>
        {label && <span className="text-[9px] text-slate-400 uppercase tracking-wide">{label}</span>}
      </div>
    </div>
  );
}

function KpiCard({ icon, label, value, accent, sublabel, loading }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 flex items-start gap-4 hover:shadow-md transition-shadow">
      <div
        className="w-11 h-11 rounded-xl flex items-center justify-center text-xl flex-shrink-0"
        style={{ backgroundColor: `${accent}15`, color: accent }}
      >
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide truncate">{label}</p>
        {loading ? (
          <Skeleton h="h-7" w="w-20" className="mt-1.5" />
        ) : (
          <p className="text-2xl font-bold text-slate-900 mt-0.5 tabular-nums">{value}</p>
        )}
        {sublabel && !loading && <p className="text-[11px] text-slate-400 mt-1">{sublabel}</p>}
      </div>
    </div>
  );
}

function StockHealthBar({ assigned, remaining }) {
  const pct = assigned > 0 ? Math.round((remaining / assigned) * 100) : 0;
  const color = pct >= 90 ? GREEN : pct >= 60 ? AMBER : RED;
  const bgClass = pct >= 90 ? "bg-emerald-50" : pct >= 60 ? "bg-amber-50" : "bg-rose-50";
  return (
    <div className="flex items-center gap-2.5 min-w-[120px]">
      <div className={`flex-1 h-2 rounded-full ${bgClass} overflow-hidden`}>
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${pct}%`, backgroundColor: color }}
        />
      </div>
      <span className="text-xs font-bold tabular-nums" style={{ color, minWidth: "2.5rem", textAlign: "right" }}>
        {pct}%
      </span>
    </div>
  );
}

/* ─────────────────────────────────────────────
   GLOBAL FILTER BAR
───────────────────────────────────────────── */
function Field({ label, children }) {
  return (
    <div className="flex flex-col gap-1.5 min-w-[140px]">
      <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">{label}</label>
      {children}
    </div>
  );
}

const selectCls =
  "text-sm border border-slate-200 rounded-xl px-3 py-1.5 bg-slate-50 text-slate-800 font-semibold focus:outline-none focus:ring-2 focus:ring-teal-400 transition";

function FilterBar({ filters, campOptions, technicianOptions, onApply, onReset, onExport }) {
  const [draft, setDraft] = useState(filters);

  useEffect(() => {
    setDraft(filters);
  }, [filters]);

  const currentYear = new Date().getFullYear();
  const years = [currentYear - 1, currentYear, currentYear + 1];
  const update = (key, value) => setDraft((d) => ({ ...d, [key]: value }));

  return (
    <div className="bg-white border border-slate-200 rounded-2xl px-5 py-4 shadow-sm">
      <div className="flex flex-wrap items-end gap-4">
        <Field label="Day">
          <input
            type="date"
            value={draft.day}
            onChange={(e) => update("day", e.target.value)}
            className={selectCls}
          />
        </Field>

        <Field label="Month">
          <select value={draft.month} onChange={(e) => update("month", Number(e.target.value))} className={selectCls}>
            {MONTH_NAMES.map((m, i) => (
              <option key={i} value={i + 1}>{m}</option>
            ))}
          </select>
        </Field>

        <Field label="Year">
          <select value={draft.year} onChange={(e) => update("year", Number(e.target.value))} className={selectCls}>
            {years.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </Field>

        <Field label="Camp">
          <select value={draft.campId} onChange={(e) => update("campId", e.target.value)} className={`${selectCls} max-w-[160px]`}>
            <option value="ALL">All Camps</option>
            {campOptions.map((c) => (
              <option key={c.camp_id ?? c.camp_location} value={c.camp_id}>
                {c.camp_location || `Camp ${c.camp_id}`}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Technician">
          <select value={draft.technicianId} onChange={(e) => update("technicianId", e.target.value)} className={`${selectCls} max-w-[160px]`}>
            <option value="ALL">All Technicians</option>
            {technicianOptions.map((t) => (
              <option key={t.technician_id} value={t.technician_id}>
                {t.technician_name ?? `Tech ${t.technician_id}`}
              </option>
            ))}
          </select>
        </Field>

        <div className="flex items-center gap-2 ml-auto">
          <button
            onClick={onReset}
            className="px-4 py-1.5 rounded-xl text-sm font-bold text-slate-500 bg-slate-100 hover:bg-slate-200 transition-colors active:scale-95"
          >
            Reset Filters
          </button>
          <button
            onClick={onExport}
            className="px-4 py-1.5 rounded-xl text-sm font-bold text-slate-600 border border-slate-200 hover:bg-slate-50 transition-colors active:scale-95"
          >
            Export Report
          </button>
          <button
            onClick={() => onApply(draft)}
            className="px-5 py-1.5 rounded-xl text-sm font-bold text-white transition-all shadow-sm hover:opacity-90 active:scale-95"
            style={{ backgroundColor: TEAL }}
          >
            Apply Filters
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   SECTION 1 — MONTHLY CONSUMPTION TREND
───────────────────────────────────────────── */
function MonthlyTrendSection({ data, loading, error, onRetry }) {
  const formatted = data.map((d) => {
    const auto = d.auto_consumed ?? 0;
    const manual = d.manual_consumed ?? 0;
    const total = d.total_consumption ?? auto + manual;
    return {
      ...d,
      label: `${MONTH_NAMES[(d.month ?? 1) - 1]} ${d.year}`,
      auto,
      manual,
      total,
    };
  });

  const stats = formatted.length
    ? {
        highest: formatted.reduce((a, b) => (b.total > a.total ? b : a)),
        lowest: formatted.reduce((a, b) => (b.total < a.total ? b : a)),
        avg: Math.round(formatted.reduce((s, d) => s + d.total, 0) / formatted.length),
      }
    : null;

  return (
    <Section title="Monthly Consumption Trend" accent={TEAL}>
      {loading ? (
        <ChartSkeleton />
      ) : error ? (
        <ErrorBlock message={error} onRetry={onRetry} />
      ) : formatted.length === 0 ? (
        <EmptyState text="No trend data available" icon="📈" />
      ) : (
        <>
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={formatted} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: "#94a3b8", fontWeight: 500 }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={36} />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: "#f8fafc" }} />
              <Legend
                iconType="circle"
                iconSize={8}
                wrapperStyle={{ fontSize: "12px", paddingTop: "16px", color: "#64748b" }}
              />
              <Bar dataKey="auto" name="Auto Consumed" fill={TEAL} radius={[4, 4, 0, 0]} maxBarSize={36} />
              <Bar dataKey="manual" name="Manual Consumed" fill={PURPLE} radius={[4, 4, 0, 0]} maxBarSize={36} />
              <Line
                type="monotone"
                dataKey="total"
                name="Total Consumption"
                stroke="#0f172a"
                strokeWidth={2.5}
                dot={{ r: 4, fill: "#0f172a" }}
              />
            </ComposedChart>
          </ResponsiveContainer>
          {stats && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-5 pt-5 border-t border-slate-100">
              <StatTile label="Highest Month" value={stats.highest.label} accent={GREEN} sub={`${stats.highest.total.toLocaleString()} units`} />
              <StatTile label="Lowest Month" value={stats.lowest.label} accent={RED} sub={`${stats.lowest.total.toLocaleString()} units`} />
              <StatTile label="Avg / Month" value={stats.avg.toLocaleString()} accent={TEAL} sub="units consumed" />
            </div>
          )}
        </>
      )}
    </Section>
  );
}

/* ─────────────────────────────────────────────
   SECTION 2 — TOP CONSUMED ITEMS
───────────────────────────────────────────── */
function TopItemsSection({ data, loading, error, onRetry }) {
  const [query, setQuery] = useState("");
  const [sortDir, setSortDir] = useState("desc");

  const filtered = data.filter(
    (i) =>
      !query ||
      i.item_name?.toLowerCase().includes(query.toLowerCase()) ||
      i.item_code?.toLowerCase().includes(query.toLowerCase())
  );
  const sorted = [...filtered].sort((a, b) =>
    sortDir === "desc"
      ? (b.total_quantity_used ?? 0) - (a.total_quantity_used ?? 0)
      : (a.total_quantity_used ?? 0) - (b.total_quantity_used ?? 0)
  );

  const maxQty = data[0]?.total_quantity_used ?? 1;
  const totalQty = data.reduce((s, i) => s + (i.total_quantity_used ?? 0), 0);

  return (
    <Section
      title="Top Consumed Items"
      accent={PURPLE}
      action={
        <div className="flex items-center gap-2">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search item…"
            className="text-xs border border-slate-200 rounded-lg px-3 py-1.5 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-purple-300 w-28 sm:w-40"
          />
          <button
            onClick={() => setSortDir((d) => (d === "desc" ? "asc" : "desc"))}
            className="text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors whitespace-nowrap"
          >
            Qty {sortDir === "desc" ? "↓" : "↑"}
          </button>
        </div>
      }
    >
      {loading ? (
        <TableSkeleton rows={6} cols={5} />
      ) : error ? (
        <ErrorBlock message={error} onRetry={onRetry} />
      ) : data.length === 0 ? (
        <EmptyState text="No item data for this period" />
      ) : sorted.length === 0 ? (
        <EmptyState text={`No items match "${query}"`} icon="🔍" />
      ) : (
        <div className="overflow-x-auto max-h-96 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-white z-10">
              <tr className="border-b border-slate-100">
                {["#", "Item", "Code", "Category", "Qty Used", "Usage %"].map((h, i) => (
                  <th
                    key={h}
                    className={`pb-3 text-xs font-semibold text-slate-400 uppercase tracking-wide bg-white whitespace-nowrap ${
                      i === 4 || i === 5 ? "text-right" : "text-left"
                    } ${i === 0 ? "w-10" : ""}`}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {sorted.map((item, idx) => {
                const rank = data.indexOf(item);
                const medal = MEDAL_COLORS[rank] ?? null;
                const pct = totalQty > 0 ? Math.round(((item.total_quantity_used ?? 0) / totalQty) * 100) : 0;
                return (
                  <tr
                    key={item.item_code ?? idx}
                    className="hover:bg-slate-50/70 transition-colors"
                    style={medal ? { borderLeft: `3px solid ${medal.border}`, backgroundColor: medal.bg } : { borderLeft: "3px solid transparent" }}
                  >
                    <td className="py-3 pl-2 pr-3">
                      {medal ? (
                        <span className="text-base leading-none">{medal.label}</span>
                      ) : (
                        <span className="text-xs text-slate-400 font-mono font-bold">{rank + 1}</span>
                      )}
                    </td>
                    <td className="py-3 pr-3">
                      <p className="font-semibold text-slate-800 text-sm leading-tight">{item.item_name}</p>
                    </td>
                    <td className="py-3 pr-3 text-[10px] text-slate-400 font-mono">{item.item_code ?? "—"}</td>
                    <td className="py-3 pr-3">
                      <CategoryBadge category={item.category} />
                    </td>
                    <td className="py-3 text-right font-bold text-slate-800 tabular-nums">
                      {(item.total_quantity_used ?? 0).toLocaleString()}
                    </td>
                    <td className="py-3 pl-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <span className="text-xs font-semibold text-slate-500 tabular-nums w-9">{pct}%</span>
                        <div className="w-14 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full"
                            style={{
                              width: `${Math.round(((item.total_quantity_used ?? 0) / maxQty) * 100)}%`,
                              backgroundColor: rank < 3 ? MEDAL_COLORS[rank].border : TEAL,
                            }}
                          />
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

/* ─────────────────────────────────────────────
   SECTION 3 — USAGE BY CAMP
───────────────────────────────────────────── */
function CampUsageSection({ data, loading, error, onRetry }) {
  const sorted = [...data].sort((a, b) => (b.total_quantity_used ?? 0) - (a.total_quantity_used ?? 0));
  const chartData = sorted.map((c) => ({
    name: c.camp_location || `Camp ${c.camp_id}`,
    used: c.total_quantity_used ?? 0,
  }));
  const top = sorted[0];
  const bottom = sorted.length > 1 ? sorted[sorted.length - 1] : null;

  return (
    <Section title="Camp-wise Inventory Consumption" accent={AMBER}>
      {loading ? (
        <ChartSkeleton />
      ) : error ? (
        <ErrorBlock message={error} onRetry={onRetry} />
      ) : data.length === 0 ? (
        <EmptyState text="No camp usage data for this month" icon="🏕" />
      ) : (
        <>
          <div className="flex flex-wrap gap-2 mb-4">
            {top && <Badge color={GREEN} label="Top Performing" value={top.camp_location || `Camp ${top.camp_id}`} />}
            {bottom && bottom !== top && (
              <Badge color={RED} label="Lowest Consumption" value={bottom.camp_location || `Camp ${bottom.camp_id}`} />
            )}
          </div>
          <div className="max-h-[260px] overflow-y-auto pr-1">
            <div style={{ height: Math.max(220, chartData.length * 46) }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} layout="vertical" margin={{ top: 4, right: 24, left: 8, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={110}
                    tick={{ fontSize: 12, fill: "#475569", fontWeight: 600 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip content={<CustomTooltip />} cursor={{ fill: "#f8fafc" }} />
                  <Bar dataKey="used" name="Qty Used" radius={[0, 6, 6, 0]} maxBarSize={22}>
                    {chartData.map((_, i) => (
                      <Cell key={i} fill={CAMP_BAR_COLORS[i % CAMP_BAR_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}
    </Section>
  );
}

/* ─────────────────────────────────────────────
   SECTION 4 — DAILY VERIFICATION STATUS
───────────────────────────────────────────── */
function DailyVerificationSection({ data, loading, error, onRetry }) {
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");

  const total = data.length;
  const submitted = data.filter((d) => d.status === "Submitted").length;
  const pending = total - submitted;
  const pct = total > 0 ? (submitted / total) * 100 : 0;

  const filtered = data.filter((d) => {
    const matchQuery = !query || d.technician_name?.toLowerCase().includes(query.toLowerCase());
    const matchStatus = statusFilter === "ALL" || d.status === statusFilter;
    return matchQuery && matchStatus;
  });

  return (
    <Section
      title="Daily Inventory Verification"
      accent={TEAL}
      action={
        <div className="flex items-center gap-2">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search technician…"
            className="text-xs border border-slate-200 rounded-lg px-3 py-1.5 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-teal-300 w-28 sm:w-40"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-slate-50 font-semibold text-slate-600"
          >
            <option value="ALL">All</option>
            <option value="Submitted">Submitted</option>
            <option value="Pending">Pending</option>
          </select>
        </div>
      }
    >
      {loading ? (
        <TableSkeleton rows={5} cols={4} />
      ) : error ? (
        <ErrorBlock message={error} onRetry={onRetry} />
      ) : data.length === 0 ? (
        <EmptyState text="No verification records for this day" icon="🗓" />
      ) : (
        <div className="flex flex-col lg:flex-row gap-6">
          <div className="flex flex-col items-center justify-center gap-4 lg:border-r lg:border-slate-100 lg:pr-6 flex-shrink-0">
            <CircularProgress pct={pct} color={pct >= 80 ? GREEN : pct >= 50 ? AMBER : RED} label="Submitted" />
            <div className="grid grid-cols-3 gap-4">
              <MiniStat label="Total" value={total} />
              <MiniStat label="Submitted" value={submitted} accent={GREEN} />
              <MiniStat label="Pending" value={pending} accent={RED} />
            </div>
          </div>
          <div className="flex-1 overflow-x-auto">
            {filtered.length === 0 ? (
              <EmptyState text="No technicians match your filters" icon="🔍" />
            ) : (
              <div className="max-h-[260px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-white z-10">
                    <tr className="border-b border-slate-100">
                      {["Technician", "Status", "Submitted Time", "Verification"].map((h, i) => (
                        <th
                          key={h}
                          className={`pb-3 text-xs font-semibold text-slate-400 uppercase tracking-wide whitespace-nowrap ${
                            i === 2 ? "text-right" : i === 3 ? "text-center" : "text-left"
                          }`}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {filtered.map((d, idx) => (
                      <tr key={d.technician_id ?? idx} className="hover:bg-slate-50/50 transition-colors">
                        <td className="py-3 pr-3 font-semibold text-slate-800 whitespace-nowrap">
                          {d.technician_name ?? `Tech ${d.technician_id}`}
                        </td>
                        <td className="py-3 pr-3">
                          <StatusPill status={d.status} />
                        </td>
                        <td className="py-3 pr-3 text-right text-xs text-slate-500 tabular-nums whitespace-nowrap">
                          {d.confirmed_at ? new Date(d.confirmed_at).toLocaleString() : "—"}
                        </td>
                        <td className="py-3 pl-3 text-center">
                          {d.status === "Submitted" ? (
                            <span className="text-emerald-500 text-base">✓</span>
                          ) : (
                            <span className="text-rose-400 text-base">✕</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </Section>
  );
}

/* ─────────────────────────────────────────────
   SECTION 5 — MONTHLY VERIFICATION & RECONCILIATION
───────────────────────────────────────────── */
function MonthlyVerificationSection({ bags, selectedBagId, onBagChange, data, loading, error, onRetry }) {
  const items = data?.items ?? [];

  const totals = items.reduce(
    (acc, it) => {
      acc.assigned += it.assigned_quantity ?? 0;
      acc.used += it.used_quantity ?? 0;
      acc.remaining += it.remaining_quantity ?? 0;
      acc.difference += it.difference ?? 0;
      return acc;
    },
    { assigned: 0, used: 0, remaining: 0, difference: 0 }
  );

  const verifiedCount = items.filter((i) => i.status === "VERIFIED").length;
  const verifiedPct = items.length ? (verifiedCount / items.length) * 100 : 0;

  const pieData = [
    { name: "Used", value: totals.used, color: PURPLE },
    { name: "Remaining", value: totals.remaining, color: TEAL },
  ];

  const maxAbsDiff = Math.max(...items.map((i) => Math.abs(i.difference ?? 0)), 1);

  return (
    <Section
      title="Monthly Verification & Reconciliation"
      accent={PURPLE}
      action={
        <select
          value={selectedBagId ?? ""}
          onChange={(e) => onBagChange(e.target.value)}
          className="text-xs border border-slate-200 rounded-lg px-3 py-1.5 bg-slate-50 font-semibold text-slate-600 max-w-[220px]"
        >
          {bags.length === 0 && <option value="">No bags available</option>}
          {bags.map((b) => (
            <option key={b.id} value={b.id}>
              {bagLabel(b)}
            </option>
          ))}
        </select>
      }
    >
      {loading ? (
        <TableSkeleton rows={5} cols={6} />
      ) : error ? (
        <ErrorBlock message={error} onRetry={onRetry} />
      ) : items.length === 0 ? (
        <EmptyState text="No verification data for this bag" icon="🧾" />
      ) : (
        <div className="space-y-5">
          {data?.technician && (
            <p className="text-sm text-slate-500">
              Technician: <span className="font-semibold text-slate-700">{data.technician}</span>
            </p>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatTile label="Total Assigned" value={totals.assigned.toLocaleString()} accent={TEAL} />
            <StatTile label="Total Used" value={totals.used.toLocaleString()} accent={PURPLE} />
            <StatTile label="Total Remaining" value={totals.remaining.toLocaleString()} accent={CYAN} />
            <StatTile
              label="Total Difference"
              value={totals.difference > 0 ? `+${totals.difference}` : totals.difference}
              accent={totals.difference === 0 ? GREEN : AMBER}
            />
          </div>

          <div className="grid lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100">
                    {["Item", "Assigned", "Used", "Remaining", "Expected", "Diff", "Status"].map((h, i) => (
                      <th
                        key={h}
                        className={`pb-3 text-xs font-semibold text-slate-400 uppercase tracking-wide whitespace-nowrap ${
                          i > 0 && i < 6 ? "text-right" : i === 6 ? "text-right" : "text-left"
                        }`}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {items.map((it, idx) => (
                    <tr key={it.inventory_item_id ?? idx} className="hover:bg-slate-50/50 transition-colors">
                      <td className="py-3 pr-2 font-semibold text-slate-800 whitespace-nowrap">{it.item_name}</td>
                      <td className="py-3 px-2 text-right tabular-nums text-slate-600">{it.assigned_quantity}</td>
                      <td className="py-3 px-2 text-right tabular-nums text-slate-600">{it.used_quantity}</td>
                      <td className="py-3 px-2 text-right tabular-nums text-slate-600">{it.remaining_quantity}</td>
                      <td className="py-3 px-2 text-right tabular-nums text-slate-400">{it.expected_remaining}</td>
                      <td
                        className="py-3 px-2 text-right tabular-nums font-bold"
                        style={{ color: it.difference === 0 ? GREEN : RED }}
                      >
                        {it.difference > 0 ? `+${it.difference}` : it.difference}
                      </td>
                      <td className="py-3 pl-2 text-right">
                        <StatusPill status={it.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="mt-5">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Difference Heatmap</p>
                <div className="flex flex-wrap gap-1.5">
                  {items.map((it, idx) => {
                    const diff = it.difference ?? 0;
                    const intensity = Math.abs(diff) / maxAbsDiff;
                    const bg = diff === 0 ? "#dcfce7" : `rgba(220,38,38,${0.15 + intensity * 0.65})`;
                    const fg = diff === 0 ? "#166534" : "#7f1d1d";
                    return (
                      <div
                        key={idx}
                        title={`${it.item_name}: ${diff > 0 ? "+" : ""}${diff}`}
                        className="w-9 h-9 rounded-lg flex items-center justify-center text-[10px] font-bold cursor-default flex-shrink-0"
                        style={{ backgroundColor: bg, color: fg }}
                      >
                        {(it.item_name ?? "?").slice(0, 2).toUpperCase()}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="flex flex-col items-center justify-center gap-4">
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={70} paddingAngle={3}>
                    {pieData.map((p, i) => (
                      <Cell key={i} fill={p.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex gap-4 text-xs">
                <LegendDot color={PURPLE} label="Used" />
                <LegendDot color={TEAL} label="Remaining" />
              </div>
              <div className="w-full">
                <div className="flex justify-between text-xs font-semibold text-slate-500 mb-1.5">
                  <span>Verification Progress</span>
                  <span>{Math.round(verifiedPct)}%</span>
                </div>
                <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{ width: `${verifiedPct}%`, backgroundColor: GREEN }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Section>
  );
}

/* ─────────────────────────────────────────────
   SECTION 6 — TECHNICIAN INVENTORY HEALTH
───────────────────────────────────────────── */
function TechnicianHealthSection({ data, loading, error, onRetry }) {
  const [sortDir, setSortDir] = useState("asc"); // asc = worst health first (needs attention)

  const withHealth = data.map((t) => {
    const pct = (t.total_assigned ?? 0) > 0 ? Math.round(((t.total_remaining ?? 0) / (t.total_assigned ?? 1)) * 100) : 0;
    return { ...t, _healthPct: pct };
  });
  const sorted = [...withHealth].sort((a, b) => (sortDir === "asc" ? a._healthPct - b._healthPct : b._healthPct - a._healthPct));

  const statusBadge = (status) => {
    const map = {
      ACTIVE: { cls: "bg-emerald-100 text-emerald-700 border-emerald-200", label: "Active" },
      CLOSED: { cls: "bg-slate-100 text-slate-600 border-slate-200", label: "Closed" },
      TRANSFERRED: { cls: "bg-blue-100 text-blue-700 border-blue-200", label: "Transferred" },
    };
    const cfg = map[status] ?? map.CLOSED;
    return (
      <span className={`inline-flex px-2 py-0.5 rounded-md text-[10px] font-bold border uppercase tracking-wide whitespace-nowrap ${cfg.cls}`}>
        {cfg.label}
      </span>
    );
  };

  return (
    <Section
      title="Technician Inventory Health"
      accent={GREEN}
      action={
        <button
          onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
          className="text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors whitespace-nowrap"
        >
          Health {sortDir === "asc" ? "↑" : "↓"}
        </button>
      }
    >
      {loading ? (
        <TableSkeleton rows={5} cols={6} />
      ) : error ? (
        <ErrorBlock message={error} onRetry={onRetry} />
      ) : data.length === 0 ? (
        <EmptyState text="No technician data for this period" />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100">
                {[
                  { label: "Technician", align: "left" },
                  { label: "Status", align: "left" },
                  { label: "Assigned", align: "right" },
                  { label: "Remaining", align: "right" },
                  { label: "Used", align: "right" },
                  { label: "Stock Health", align: "left" },
                  { label: "Low Stock", align: "center" },
                ].map(({ label, align }) => (
                  <th
                    key={label}
                    className={`pb-3 text-xs font-semibold text-slate-400 uppercase tracking-wide text-${align} whitespace-nowrap px-2 first:pl-0 last:pr-0`}
                  >
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {sorted.map((tech, idx) => (
                <tr key={tech.technician_id ?? idx} className="hover:bg-slate-50/50 transition-colors">
                  <td className="py-3.5 pr-3 pl-0">
                    <div className="flex items-center gap-2.5">
                      <div
                        className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                        style={{ backgroundColor: CAMP_BAR_COLORS[idx % CAMP_BAR_COLORS.length] }}
                      >
                        {(tech.technician_name ?? "?")[0].toUpperCase()}
                      </div>
                      <span className="font-semibold text-slate-800 whitespace-nowrap">
                        {tech.technician_name ?? `Tech ${tech.technician_id}`}
                      </span>
                    </div>
                  </td>
                  <td className="py-3.5 px-2">{statusBadge(tech.bag_status)}</td>
                  <td className="py-3.5 px-2 text-right tabular-nums font-medium text-slate-700">
                    {(tech.total_assigned ?? 0).toLocaleString()}
                  </td>
                  <td className="py-3.5 px-2 text-right tabular-nums font-medium text-slate-700">
                    {(tech.total_remaining ?? 0).toLocaleString()}
                  </td>
                  <td className="py-3.5 px-2 text-right tabular-nums font-bold" style={{ color: PURPLE }}>
                    {(tech.total_used ?? 0).toLocaleString()}
                  </td>
                  <td className="py-3.5 px-2 min-w-[160px]">
                    <StockHealthBar assigned={tech.total_assigned ?? 0} remaining={tech.total_remaining ?? 0} />
                  </td>
                  <td className="py-3.5 pl-2 pr-0 text-center">
                    {(tech.items_low_stock_count ?? 0) > 0 ? (
                      <span className="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-1.5 rounded-full text-xs font-bold bg-rose-100 text-rose-700 border border-rose-200">
                        {tech.items_low_stock_count}
                      </span>
                    ) : (
                      <span className="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-1.5 rounded-full text-xs font-bold bg-slate-100 text-slate-400">
                        0
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

/* ─────────────────────────────────────────────
   SECTION 7 — AI DASHBOARD INSIGHTS
───────────────────────────────────────────── */
function buildInsights({ campArr, dailyArr, topItemsArr, techArr }) {
  const insights = [];

  if (campArr.length > 1) {
    const avg = campArr.reduce((s, c) => s + (c.total_quantity_used ?? 0), 0) / campArr.length;
    const top = [...campArr].sort((a, b) => (b.total_quantity_used ?? 0) - (a.total_quantity_used ?? 0))[0];
    if (avg > 0 && top) {
      const pctAbove = Math.round(((top.total_quantity_used - avg) / avg) * 100);
      if (pctAbove > 5) {
        insights.push({
          icon: "📈",
          text: `${top.camp_location || `Camp ${top.camp_id}`} consumed ${pctAbove}% more inventory than the average camp this month.`,
        });
      }
    }
  }

  const pendingTechs = dailyArr.filter((d) => d.status === "Pending").length;
  if (pendingTechs > 0) {
    insights.push({
      icon: "⏰",
      text: `${pendingTechs} technician${pendingTechs > 1 ? "s" : ""} have not submitted daily verification yet.`,
    });
  }

  if (topItemsArr[0]) {
    insights.push({
      icon: "🧪",
      text: `${topItemsArr[0].item_name} remains the most consumed item this period.`,
    });
  }

  const criticalTechs = techArr.filter((t) => {
    const pct = (t.total_assigned ?? 0) > 0 ? ((t.total_remaining ?? 0) / (t.total_assigned ?? 1)) * 100 : 0;
    return pct < 60;
  }).length;
  if (criticalTechs > 0) {
    const isPlural = criticalTechs > 1;
    insights.push({
      icon: "🚨",
      text: `${criticalTechs} technician${isPlural ? "s" : ""} ${isPlural ? "have" : "has"} critical stock levels and ${isPlural ? "need" : "needs"} restocking.`,
    });
  }

  return insights;
}

function AIInsightsSection({ insights }) {
  return (
    <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl p-6 shadow-lg relative overflow-hidden">
      <div
        className="absolute inset-0 opacity-[0.08] pointer-events-none"
        style={{
          backgroundImage: `radial-gradient(circle at 20% 20%, ${TEAL}, transparent 40%), radial-gradient(circle at 80% 80%, ${PURPLE}, transparent 40%)`,
        }}
      />
      <div className="relative">
        <div className="flex items-center gap-2 mb-5">
          <span className="text-lg">✨</span>
          <h2 className="text-sm font-bold text-white uppercase tracking-wide">Dashboard Insights</h2>
        </div>
        {insights.length === 0 ? (
          <p className="text-sm text-slate-400">Not enough data yet to generate insights for this period.</p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            {insights.map((ins, idx) => (
              <div
                key={idx}
                className="bg-white/[0.06] backdrop-blur border border-white/10 rounded-xl p-4 flex items-start gap-3 hover:bg-white/[0.09] transition-colors"
              >
                <span className="text-base flex-shrink-0">{ins.icon}</span>
                <p className="text-sm text-slate-200 leading-snug">{ins.text}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   MAIN PAGE
───────────────────────────────────────────── */
export default function InventoryAnalytics() {
  const now = new Date();
  const todayStr = now.toISOString().slice(0, 10);
  const defaultFilters = {
    day: todayStr,
    month: now.getMonth() + 1,
    year: now.getFullYear(),
    campId: "ALL",
    technicianId: "ALL",
  };

  const [filters, setFilters] = useState(defaultFilters);
  const [filterKey, setFilterKey] = useState(0);
  const [selectedBagId, setSelectedBagId] = useState(null);

  const handleApply = (next) => {
    setFilters(next);
    setFilterKey((k) => k + 1);
  };
  const handleReset = () => {
    setFilters(defaultFilters);
    setFilterKey((k) => k + 1);
  };

  /* ---- DATA FETCHING (filters drive every widget) ---- */
  const trend = useFetch(() => inventoryApi.getMonthlyTrend({ months_back: 6 }), [filterKey]);
  const topItems = useFetch(
    () => inventoryApi.getTopItems({ limit: 10, month: filters.month, year: filters.year }),
    [filters.month, filters.year, filterKey]
  );
  const campSummary = useFetch(
    () => inventoryApi.getCampSummary({ month: filters.month, year: filters.year }),
    [filters.month, filters.year, filterKey]
  );
  const technicianSummary = useFetch(
    () => inventoryApi.getTechnicianSummary({ month: filters.month, year: filters.year }),
    [filters.month, filters.year, filterKey]
  );
  const dailyStatus = useFetch(
    () =>
      inventoryApi.getDailyUsageStatus({
        date: filters.day,
        ...(filters.campId !== "ALL" ? { camp_id: filters.campId } : {}),
      }),
    [filters.day, filters.campId, filterKey]
  );
  const alerts = useFetch(() => inventoryApi.getUnresolvedAlerts(), [filterKey]);
  const bagsList = useFetch(() => inventoryApi.getBags({ status: "ACTIVE" }), [filterKey]);

  const bagsArr = normalizeArr(bagsList.data);

  // Auto-pick a bag for the reconciliation section: prefer one matching the
  // selected technician filter, otherwise fall back to the first active bag.
  useEffect(() => {
    if (bagsArr.length === 0) return;
    if (filters.technicianId !== "ALL") {
      const match = bagsArr.find((b) => String(b.technician_id ?? b.technician) === String(filters.technicianId));
      if (match) {
        setSelectedBagId(match.id);
        return;
      }
    }
    setSelectedBagId((prev) => prev ?? bagsArr[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bagsList.data, filters.technicianId]);

  const monthlyVerification = useFetch(
    () => (selectedBagId ? inventoryApi.getMonthlyVerification(selectedBagId) : Promise.resolve(null)),
    [selectedBagId]
  );

  /* ---- NORMALIZE + FILTER ---- */
  const campFull = normalizeArr(campSummary.data);
  const campArr =
    filters.campId === "ALL"
      ? campFull
      : campFull.filter((c) => String(c.camp_id) === String(filters.campId));

  const techFull = normalizeArr(technicianSummary.data);
  const techArr =
    filters.technicianId === "ALL"
      ? techFull
      : techFull.filter((t) => String(t.technician_id) === String(filters.technicianId));

  const topItemsArr = normalizeArr(topItems.data);
  const dailyArr = normalizeArr(dailyStatus.data);
  const alertsArr = normalizeArr(alerts.data);

  const trendArr = normalizeArr(trend.data).map((d) => ({
    ...d,
    total: d.total_consumption ?? (d.auto_consumed ?? 0) + (d.manual_consumed ?? 0),
  }));

  /* ---- KPI CALCULATIONS ---- */
  const totalUsed = techFull.reduce((s, t) => s + (t.total_used ?? 0), 0);
  const activeTechCount = techFull.filter((t) => (t.bag_status ?? "ACTIVE") === "ACTIVE").length;
  const pendingCount = dailyArr.filter((d) => d.status === "Pending").length;
  const lowStockCount = alertsArr.length;

  let usedTrendPct = null;
  if (trendArr.length >= 2) {
    const last = trendArr[trendArr.length - 1].total ?? 0;
    const prev = trendArr[trendArr.length - 2].total ?? 0;
    if (prev > 0) usedTrendPct = Math.round(((last - prev) / prev) * 100);
  }

  const insights = buildInsights({ campArr: campFull, dailyArr, topItemsArr, techArr: techFull });

  /* ---- EXPORT ---- */
  const handleExport = () => {
    const csv = buildCsv(
      techFull.map((t) => ({
        Technician: t.technician_name,
        Status: t.bag_status,
        Assigned: t.total_assigned,
        Used: t.total_used,
        Remaining: t.total_remaining,
        LowStockCount: t.items_low_stock_count,
      })),
      ["Technician", "Status", "Assigned", "Used", "Remaining", "LowStockCount"]
    );
    downloadCsv(`inventory-report-${filters.month}-${filters.year}.csv`, csv);
  };

  return (
    <>
      <style>{`
        @keyframes fade-up {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .fade-up { animation: fade-up 0.35s ease-out both; }
      `}</style>

      <div className="min-h-screen bg-slate-50 font-sans">
        {/* ── PAGE HEADER ── */}
        <div className="bg-white border-b border-slate-100 px-6 py-5">
          <div className="max-w-7xl mx-auto flex items-center gap-3">
            <div
              className="w-11 h-11 rounded-xl flex items-center justify-center text-white text-xl shadow-md flex-shrink-0"
              style={{ background: `linear-gradient(135deg, ${TEAL}, ${PURPLE})` }}
            >
              📊
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900 leading-tight">Inventory Analytics Dashboard</h1>
              <p className="text-xs text-slate-400 mt-0.5">
                Track inventory consumption, technician compliance, and stock health across all camps.
              </p>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-5">
          {/* ── KPI CARDS ── */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 fade-up" style={{ animationDelay: "0ms" }}>
            <KpiCard
              icon="📦"
              label="Total Inventory Used"
              accent={TEAL}
              loading={technicianSummary.loading}
              value={totalUsed.toLocaleString()}
              sublabel={usedTrendPct != null ? `${usedTrendPct >= 0 ? "▲" : "▼"} ${Math.abs(usedTrendPct)}% vs last month` : "this period"}
            />
            <KpiCard
              icon="👷"
              label="Active Technicians"
              accent={PURPLE}
              loading={technicianSummary.loading}
              value={activeTechCount}
              sublabel={`of ${techFull.length} total`}
            />
            <KpiCard
              icon="🕒"
              label="Pending Verifications"
              accent={AMBER}
              loading={dailyStatus.loading}
              value={pendingCount}
              sublabel={dailyArr.length ? `${Math.round((pendingCount / dailyArr.length) * 100)}% of technicians` : "no data for this day"}
            />
            <KpiCard
              icon="⚠️"
              label="Low Stock Alerts"
              accent={RED}
              loading={alerts.loading}
              value={lowStockCount}
              sublabel={lowStockCount > 0 ? "needs attention" : "all clear"}
            />
          </div>

          {/* ── GLOBAL FILTERS ── */}
          <div className="fade-up" style={{ animationDelay: "40ms" }}>
            <FilterBar
              filters={filters}
              campOptions={campFull}
              technicianOptions={techFull}
              onApply={handleApply}
              onReset={handleReset}
              onExport={handleExport}
            />
          </div>

          {/* ── SECTION 1: MONTHLY CONSUMPTION TREND ── */}
          <div className="fade-up" style={{ animationDelay: "80ms" }}>
            <MonthlyTrendSection data={trendArr} loading={trend.loading} error={trend.error} onRetry={trend.refetch} />
          </div>

          {/* ── SECTION 2 + 3: TOP ITEMS / USAGE BY CAMP ── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="fade-up" style={{ animationDelay: "120ms" }}>
              <TopItemsSection data={topItemsArr} loading={topItems.loading} error={topItems.error} onRetry={topItems.refetch} />
            </div>
            <div className="fade-up" style={{ animationDelay: "160ms" }}>
              <CampUsageSection data={campArr} loading={campSummary.loading} error={campSummary.error} onRetry={campSummary.refetch} />
            </div>
          </div>

          {/* ── SECTION 4: DAILY VERIFICATION STATUS ── */}
          <div className="fade-up" style={{ animationDelay: "200ms" }}>
            <DailyVerificationSection data={dailyArr} loading={dailyStatus.loading} error={dailyStatus.error} onRetry={dailyStatus.refetch} />
          </div>

          {/* ── SECTION 5: MONTHLY VERIFICATION & RECONCILIATION ── */}
          <div className="fade-up" style={{ animationDelay: "240ms" }}>
            <MonthlyVerificationSection
              bags={bagsArr}
              selectedBagId={selectedBagId}
              onBagChange={(id) => setSelectedBagId(id ? Number(id) : null)}
              data={monthlyVerification.data}
              loading={monthlyVerification.loading || bagsList.loading}
              error={monthlyVerification.error || bagsList.error}
              onRetry={monthlyVerification.refetch}
            />
          </div>

          {/* ── SECTION 6: TECHNICIAN INVENTORY HEALTH ── */}
          <div className="fade-up" style={{ animationDelay: "280ms" }}>
            <TechnicianHealthSection
              data={techArr}
              loading={technicianSummary.loading}
              error={technicianSummary.error}
              onRetry={technicianSummary.refetch}
            />
          </div>

          {/* ── SECTION 7: AI DASHBOARD INSIGHTS ── */}
          <div className="fade-up" style={{ animationDelay: "320ms" }}>
            <AIInsightsSection insights={insights} />
          </div>
        </div>
      </div>
    </>
  );
}